import {
  Box,
  Card,
  CardContent,
  Stack,
  IconButton,
  Typography,
  Grid,
  Divider,
  Chip,
} from "@mui/material";
import {
  Cancel,
  Person,
  Email,
  TrendingUp,
  CalendarToday,
  WorkOutline,
  Star,
} from "@mui/icons-material";
import MDTypography from "components/MDTypography";
import CustomDataGrid from "components/CustomDataGrid";
import GenericModal from "components/GenericModal";
import moment from "moment";

const FilledByDetailModal = ({ open, onClose, agentData }) => {
  if (!agentData) return null;

  const positionRows =
    agentData.positions?.map((position, index) => ({
      id: index + 1,
      applicant: position.applicantName,
      filledDate: position.filledDate,
      jobSlug: position.jobSlug,
      daysAgo: moment().diff(moment(position.filledDate), "days"),
    })) || [];

  const recentFills = positionRows.filter((p) => p.daysAgo <= 7).length;
  const mostRecentFill =
    positionRows.length > 0
      ? positionRows.sort((a, b) => new Date(b.filledDate) - new Date(a.filledDate))[0]
      : null;

  const getDaysAgoText = (days) => {
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return `${days} days ago`;
  };

  const positionColumns = [
    {
      field: "applicant",
      headerName: "Applicant",
      flex: 1.5,
      renderCell: ({ value }) => (
        <MDTypography variant="body2" fontWeight="medium">
          {value}
        </MDTypography>
      ),
    },
    {
      field: "filledDate",
      headerName: "Fill Date",
      flex: 1,
      renderCell: ({ value, row }) => (
        <Box>
          <MDTypography variant="body2" fontWeight="medium">
            {moment(value).format("MMM DD, YYYY")}
          </MDTypography>
          <MDTypography variant="caption" color="text.secondary">
            {getDaysAgoText(row.daysAgo)}
          </MDTypography>
        </Box>
      ),
    },
    {
      field: "jobSlug",
      headerName: "Position",
      flex: 1.2,
      renderCell: ({ value }) => (
        <Chip
          label={value?.replace(/-/g, " ")?.toUpperCase() || "Unknown"}
          size="small"
          sx={{
            bgcolor: "#E3F2FD",
            color: "#1976D2",
            fontWeight: "medium",
            fontSize: "0.75rem",
          }}
        />
      ),
    },
  ];

  // Minimalist header with clean styling
  const header = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        p: 3,
        borderBottom: "1px solid #E0E0E0",
        bgcolor: "#FAFAFA",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Person sx={{ color: "#6B7280", fontSize: 28 }} />
        <Box>
          <MDTypography variant="h5" fontWeight="bold" color="text.primary">
            {agentData.agent}
          </MDTypography>
          {agentData.email && (
            <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
              <Email sx={{ fontSize: 16, color: "#6B7280" }} />
              <MDTypography variant="body2" color="text.secondary">
                {agentData.email}
              </MDTypography>
            </Stack>
          )}
        </Box>
      </Stack>
      <IconButton
        onClick={onClose}
        sx={{
          color: "#6B7280",
          "&:hover": { bgcolor: "#F3F4F6" },
        }}
      >
        <Cancel />
      </IconButton>
    </Box>
  );

  const modalBody = (
    <Box sx={{ p: 3 }}>
      {/* Stats Cards Grid - Same Height */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              border: "1px solid #E5E7EB",
              borderRadius: 2,
              "&:hover": { borderColor: "#D1D5DB" },
            }}
          >
            <CardContent
              sx={{
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Star sx={{ color: "#F59E0B", fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Total Fills
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" color="#1F2937">
                {agentData.count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              border: "1px solid #E5E7EB",
              borderRadius: 2,
              "&:hover": { borderColor: "#D1D5DB" },
            }}
          >
            <CardContent
              sx={{
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingUp sx={{ color: "#10B981", fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Recent Fills
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" color="#1F2937">
                {recentFills}
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                Last 7 days
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              border: "1px solid #E5E7EB",
              borderRadius: 2,
              "&:hover": { borderColor: "#D1D5DB" },
            }}
          >
            <CardContent
              sx={{
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CalendarToday sx={{ color: "#8B5CF6", fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Last Fill
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="#1F2937">
                {mostRecentFill ? moment(mostRecentFill.filledDate).format("MMM DD") : "N/A"}
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                {mostRecentFill ? `${mostRecentFill.daysAgo} days ago` : ""}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Positions Table */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <WorkOutline sx={{ color: "#6B7280", fontSize: 20 }} />
          <MDTypography variant="h6" fontWeight="bold" color="text.primary">
            Recent Positions Filled
          </MDTypography>
        </Stack>

        <Card elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
          <CustomDataGrid
            rows={positionRows}
            columns={positionColumns}
            pageSize={5}
            hideFooterPagination={positionRows.length <= 5}
            sx={{
              border: "none",
              "& .MuiDataGrid-cell": {
                borderColor: "#F0F0F0",
              },
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "#FAFAFA",
                borderColor: "#F0F0F0",
              },
            }}
          />
        </Card>
      </Box>
    </Box>
  );

  return (
    <GenericModal
      open={open}
      setOpen={onClose}
      header={header}
      body={modalBody}
      maxWidth="800px"
      maxHeight="650px"
      borderRadius={8}
      contentPadding={0}
      sx={(theme) => ({
        "& .MuiDialog-paper": {
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        },
      })}
    />
  );
};

export default FilledByDetailModal;
